import React, { useEffect, useRef } from 'react';

/**
 * Small in-page confirmation used across the advertiser portal instead of the
 * native window.confirm() (audit 2026-09-04-ux/business.md row 55). Renders
 * nothing when `open` is false, closes on Escape, and focuses the confirm button.
 */
const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const confirmRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    confirmRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCancel?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  if (!open) return null;

  const confirmBackground = tone === 'danger'
    ? 'linear-gradient(135deg, #ef4444, #dc2626)'
    : 'linear-gradient(135deg, #8b5cf6, #7c3aed)';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-4"
      onClick={() => { if (!busy) onCancel?.(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="bg-white w-full sm:max-w-md rounded-2xl p-6"
        style={{ boxShadow: '0 20px 50px rgba(30, 27, 75, 0.25)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-[18px] font-semibold text-[#1e1b4b] mb-2">{title}</h2>
        {message && (
          <p className="text-[14px] mb-6" style={{ color: 'rgba(30, 27, 75, 0.6)' }}>{message}</p>
        )}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-5 py-3 min-h-[44px] rounded-xl font-semibold transition-all disabled:opacity-50"
            style={{ color: 'rgba(30, 27, 75, 0.7)', border: '2px solid rgba(30, 27, 75, 0.1)' }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            ref={confirmRef}
            onClick={onConfirm}
            disabled={busy}
            className="px-5 py-3 min-h-[44px] rounded-xl font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50"
            style={{ background: confirmBackground }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
